import { board } from '@/state/board';
import { openTimers } from '@/state/data';
import { showToast } from '@/state/ui';

/** Foreground alarms: while the app is open, chime + toast the moment a
 *  feeding or timer crosses due — no network or push required. */

let lastCheck = Date.now();
let ctx: AudioContext | null = null;

function chime(urgent: boolean) {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    const t0 = ctx.currentTime;
    const notes = urgent ? [659.25, 523.25, 659.25] : [523.25, 659.25]; // E5/C5 phrases
    notes.forEach((freq, i) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t0 + i * 0.18);
      gain.gain.linearRampToValueAtTime(0.12, t0 + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.18 + 0.4);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(t0 + i * 0.18);
      osc.stop(t0 + i * 0.18 + 0.45);
    });
  } catch {
    /* audio unavailable (no gesture yet) — toast still shows */
  }
}

function check() {
  const now = Date.now();
  const since = lastCheck;
  lastCheck = now;

  const newlyDue = board.value.filter((e) => {
    const t = e.nextDue?.getTime();
    return t != null && t > since && t <= now;
  });
  const newTimers = openTimers.value.filter((t) => {
    const d = new Date(t.due_at).getTime();
    return d > since && d <= now;
  });

  if (!newlyDue.length && !newTimers.length) return;

  if (newlyDue.length) {
    const names = newlyDue.slice(0, 3).map((e) => e.animal.name).join(', ');
    const more = newlyDue.length > 3 ? ` +${newlyDue.length - 3} more` : '';
    showToast({ kind: 'info', message: `Feeding due — ${names}${more}` }, 6000);
  }
  for (const t of newTimers) {
    showToast({ kind: 'info', message: `Timer — ${t.label}` }, 6000);
  }
  chime(true);
  if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
}

export function initAlarms() {
  setInterval(check, 15_000);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) check();
  });
  // AudioContext needs a user gesture on iOS; prime it on first touch.
  const prime = () => {
    ctx ??= new AudioContext();
    void ctx.resume();
    window.removeEventListener('pointerdown', prime);
  };
  window.addEventListener('pointerdown', prime, { once: true });
}
